import { useEffect, useRef, useState } from "react";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import type { AppContext } from "../App.js";
import { useLobbyWs } from "../lib/useLobbyWs.js";
import { useFullscreen } from "../lib/uiHooks.js";
import { ChatPanel } from "../components/ChatPanel.js";
import { RulesButton } from "../components/RulesSheet.js";
import { gameByKind } from "../games/registry.js";
import type { ChatMessage } from "../lib/useLobbyWs.js";

export function GamePage() {
  const { id } = useParams<{ id: string }>();
  const ctx = useOutletContext<AppContext>();
  const ws = useLobbyWs(id ?? null);
  const navigate = useNavigate();
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [confirmLeave, setConfirmLeave] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const fs = useFullscreen();
  // Track how many chat messages had been seen the last time the chat panel
  // was open, so we can show an unread badge on the chat button.
  const [chatSeenCount, setChatSeenCount] = useState(0);
  useEffect(() => {
    if (chatOpen) setChatSeenCount(ws.chat.length);
  }, [chatOpen, ws.chat.length]);
  const unreadChat = Math.max(0, ws.chat.length - chatSeenCount);

  // Flag <body> while the game view is mounted. Mobile rotation CSS keys
  // off `body.in-game-view`, scoped so menus stay un-rotated.
  useEffect(() => {
    document.body.classList.add("in-game-view");
    return () => document.body.classList.remove("in-game-view");
  }, []);

  // Lobby closed (host disconnected, kicked, leave_game, etc.) — bail to home.
  useEffect(() => {
    if (ws.closedReason) {
      const t = setTimeout(() => navigate("/", { replace: true }), 2200);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [ws.closedReason, navigate]);

  if (ws.closedReason) {
    return (
      <main className="container">
        <div className="card-block">
          <h2 style={{ marginTop: 0 }}>Game ended</h2>
          <p className="muted">{ws.closedReason}. Returning home…</p>
        </div>
      </main>
    );
  }

  if (!ws.lobby) {
    return (
      <main className="container">
        <p className="muted">Connecting to game…</p>
        {ws.lastError && <div className="toast">{ws.lastError}</div>}
      </main>
    );
  }

  if (ws.lobby.status === "lobby") {
    // Game hasn't started — kick back to lobby.
    navigate(`/lobby/${ws.lobby.id}`, { replace: true });
    return null;
  }

  const gs = ws.gameState;
  if (!gs) {
    return (
      <main className="container">
        <p className="muted">Waiting for game state…</p>
        {ws.lastError && <div className="toast">{ws.lastError}</div>}
      </main>
    );
  }

  const game = gameByKind(gs.kind);
  const isSpectator = ws.yourSeat === null || ws.yourSeat < 0;
  const isHost = ws.lobby?.hostId === ctx.me?.id;
  // The game's "match end" condition is encoded per-game. We can also
  // detect it from `ws.summary` being set (the adapter posts a summary
  // when isFinished() returns true).
  const matchEnded = !!ws.summary;

  return (
    /* `.game-shell` is the rotation wrapper for mobile-portrait landscape
       lock — see `body.mobile-device.in-game-view` CSS in global.css.
       All in-game UI lives inside it so floating buttons rotate with
       the table instead of staying anchored to the physical viewport. */
    <div className="game-shell">
      {ws.lastError && <div className="toast">{ws.lastError}</div>}
      {isSpectator && <div className="spectator-banner">SPECTATING</div>}

      {matchEnded && ws.summary && (
        <div className="match-end-overlay">
          <h2>Match complete</h2>
          <div className="match-end-lines">
            {ws.summary.lines.map((line, i) => (
              <div key={i}>{line}</div>
            ))}
          </div>
          <div className="row" style={{ gap: 10, justifyContent: "center", marginTop: 14 }}>
            {isHost ? (
              <button
                className="primary"
                onClick={() => ws.send({ t: "return_to_lobby" })}
              >
                Play again
              </button>
            ) : (
              <span className="muted" style={{ fontSize: 13 }}>
                Waiting for the host to play again…
              </span>
            )}
            <button
              onClick={() => {
                ws.send({ t: "leave_game" });
                navigate("/", { replace: true });
              }}
            >
              Leave
            </button>
          </div>
        </div>
      )}

      {game && (
        <game.GameView
          state={gs}
          yourSeat={ws.yourSeat}
          yourPrivate={ws.privateData ?? null}
          events={ws.events}
          send={ws.send}
          mobile={ctx.isMobile}
        />
      )}

      <button
        className="options-button"
        onClick={() => setOptionsOpen((v) => !v)}
        title="Game options"
        aria-label="Game options"
      >
        ⚙
      </button>

      <RulesButton gameKind={gs.kind} className="in-game" />

      <button
        className="chat-button"
        onClick={() => setChatOpen((v) => !v)}
        title="Table chat"
        aria-label="Table chat"
      >
        💬
        {unreadChat > 0 && !chatOpen && (
          <span className="chat-unread-badge">{unreadChat > 9 ? "9+" : unreadChat}</span>
        )}
      </button>

      {chatOpen && (
        <DraggableChatOverlay
          messages={ws.chat}
          myId={ctx.me?.id ?? null}
          onSend={(text) => ws.send({ t: "chat", text })}
          onClose={() => setChatOpen(false)}
          mobile={ctx.isMobile}
        />
      )}

      {optionsOpen && (
        <div className="options-panel">
          <div className="row between" style={{ marginBottom: 10 }}>
            <strong>Options</strong>
            <button onClick={() => setOptionsOpen(false)} style={{ padding: "4px 10px" }}>Close</button>
          </div>
          <div className="muted" style={{ fontSize: 13, marginBottom: 12 }}>
            You are {isSpectator ? "spectating" : `playing as ${ctx.me?.displayName ?? "this seat"}`}.
          </div>
          {fs.supported ? (
            <button
              style={{ width: "100%", marginBottom: 10 }}
              onClick={fs.toggle}
              title={
                fs.active
                  ? "Exit fullscreen (Esc also works)"
                  : "Use the full screen — extra room for the board"
              }
            >
              {fs.active ? "Exit fullscreen" : "Enter fullscreen"}
            </button>
          ) : (
            <div
              className="muted"
              style={{ fontSize: 11, marginBottom: 10, lineHeight: 1.4 }}
            >
              Fullscreen isn't supported here. On iOS, "Add to Home Screen"
              from Safari/Chrome gives a similar full-screen launch.
            </div>
          )}
          {isHost && !matchEnded && (
            <button
              style={{ width: "100%", marginBottom: 10 }}
              onClick={() => {
                ws.send({ t: "return_to_lobby" });
                setOptionsOpen(false);
              }}
              title="Abandon the current game and return everyone to the lobby. Seats are kept."
            >
              Return to lobby (host)
            </button>
          )}
          {!confirmLeave ? (
            <button
              className="danger"
              style={{ width: "100%" }}
              onClick={() => {
                if (isSpectator) {
                  // Spectator leave is benign; no warning.
                  ws.send({ t: "leave_game" });
                  navigate("/", { replace: true });
                } else {
                  setConfirmLeave(true);
                }
              }}
            >
              {isSpectator ? "Stop spectating" : "Leave game…"}
            </button>
          ) : (
            <div>
              <p style={{ marginTop: 0, marginBottom: 8 }}>
                Leaving will <strong>end the game for everyone</strong>. Are you sure?
              </p>
              <div className="row" style={{ gap: 8 }}>
                <button
                  className="danger"
                  style={{ flex: 1 }}
                  onClick={() => {
                    ws.send({ t: "leave_game" });
                    setOptionsOpen(false);
                    setConfirmLeave(false);
                  }}
                >
                  Yes, end game
                </button>
                <button style={{ flex: 1 }} onClick={() => setConfirmLeave(false)}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          <hr style={{ margin: "14px 0 10px", border: 0, borderTop: "1px solid var(--border)" }} />
          <div className="muted" style={{ fontSize: 12, marginBottom: 6, letterSpacing: 1 }}>RECENT EVENTS</div>
          <div className="options-events">
            {game?.EventLog ? (
              <game.EventLog events={ws.events} state={gs} />
            ) : (
              <div className="event-log">
                {ws.events.slice(-12).map((e, i) => (
                  <div key={i} className="line">{e.text ?? e.type}</div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Floating chat window that can be dragged around the viewport by its header
 * on desktop. Mobile keeps the original anchored position — small screens
 * have a tightly-tuned rotated CSS layout that a drag offset would fight.
 *
 * Position is held in local state (per-mount): closing and re-opening the
 * chat resets to the default anchor. Pointer events are used so the same
 * code path works for mouse + touchpad without competing with touch CSS.
 */
function DraggableChatOverlay({
  messages,
  myId,
  onSend,
  onClose,
  mobile,
}: {
  messages: ChatMessage[];
  myId: string | null;
  onSend: (text: string) => void;
  onClose: () => void;
  mobile: boolean;
}) {
  // Offset from the default anchored position (CSS `bottom: 74px; right: 18px`).
  // A positive dx moves right, positive dy moves down.
  const [pos, setPos] = useState<{ dx: number; dy: number } | null>(null);
  const dragRef = useRef<{ startX: number; startY: number; baseDx: number; baseDy: number; pointerId: number } | null>(null);
  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (mobile) return; // anchored on mobile
    if (e.button !== 0 && e.pointerType === "mouse") return;
    // Don't start a drag from interactive children (e.g. the Close button).
    const target = e.target as HTMLElement;
    if (target.closest("button, input, textarea, a")) return;
    e.preventDefault();
    const cur = pos ?? { dx: 0, dy: 0 };
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      baseDx: cur.dx,
      baseDy: cur.dy,
      pointerId: e.pointerId,
    };
    (e.currentTarget as HTMLDivElement).setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    setPos({
      dx: drag.baseDx + (e.clientX - drag.startX),
      dy: drag.baseDy + (e.clientY - drag.startY),
    });
  };
  const endDrag = (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    dragRef.current = null;
    try {
      (e.currentTarget as HTMLDivElement).releasePointerCapture(e.pointerId);
    } catch {/* ignore */}
  };
  // CSS anchors to bottom-right; translate by the recorded offset so dragging
  // feels natural in viewport pixels regardless of the overlay's intrinsic size.
  const style: React.CSSProperties | undefined =
    pos && !mobile
      ? { transform: `translate(${pos.dx}px, ${pos.dy}px)` }
      : undefined;
  return (
    <div className="chat-overlay" style={style}>
      <div
        className="chat-overlay-header"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        title={mobile ? undefined : "Drag to move"}
      >
        <strong>Table chat</strong>
        <button onClick={onClose} style={{ padding: "4px 10px" }}>Close</button>
      </div>
      <ChatPanel
        messages={messages}
        myId={myId}
        onSend={onSend}
        height={300}
        autoFocus
      />
    </div>
  );
}
